/**
 * Eine verknüpfte Vorschrift nachlesen: Wortlaut (nur frei veröffentlichte Regelwerke) und Link auf
 * die amtliche Seite — ohne die Werkstatt zu verlassen.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ExternalLink } from "lucide-react";

import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { useGuidelinesAPI, type ProvisionView } from "./GuidelinesAPI";

export function ProvisionDialog(props: {
  customer: string;
  documentId: string;
  target: { regulationId: string; provisionId: string | null } | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const api = useGuidelinesAPI(props.customer);
  const [data, setData] = useState<ProvisionView | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Zitiert die Kante das ganze Regelwerk, wählt der Leser hier eine Vorschrift aus dessen Inhaltsverzeichnis.
  const [chosen, setChosen] = useState<string | null>(null);
  const [contents, setContents] = useState<ProvisionView | null>(null);
  const target = props.target;
  const provisionId = target?.provisionId ?? chosen;

  useEffect(() => {
    setChosen(null);
    setContents(null);
  }, [target?.regulationId, target?.provisionId]);

  useEffect(() => {
    if (!target) return;
    let live = true;
    setData(null);
    setError(null);
    api.getProvision(props.documentId, target.regulationId, provisionId).then(
      (view) => {
        if (!live) return;
        setData(view);
        if (!provisionId) setContents(view);
      },
      (e) => live && setError(e instanceof Error ? e.message : String(e)),
    );
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.documentId, target?.regulationId, provisionId]);

  const list = !provisionId ? data?.provisions ?? [] : [];

  return (
    <Dialog open={!!target} onOpenChange={props.onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-3xl flex-col">
        <DialogHeader>
          <DialogTitle>{data?.heading ?? t("guidelinesPage.provision.loading")}</DialogTitle>
          <DialogDescription>
            {data ? [data.title && !data.heading.includes(data.title) ? data.title : null, data.regulationTitle].filter(Boolean).join(" · ") : null}
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto rounded-md border bg-muted/30 p-4 text-sm leading-relaxed">
          {error ? (
            <p className="text-destructive">{error}</p>
          ) : !data ? (
            <p className="text-muted-foreground">{t("guidelinesPage.provision.loading")}</p>
          ) : data.repealed ? (
            <p className="text-muted-foreground">{t("guidelinesPage.provision.repealed")}</p>
          ) : list.length ? (
            <div className="flex flex-col gap-1">
              <p className="mb-2 text-xs text-muted-foreground">{t("guidelinesPage.provision.contentsHint")}</p>
              {list.map((p) => (
                <button
                  key={p.provisionId}
                  type="button"
                  className="flex gap-3 rounded px-2 py-1 text-left hover:bg-muted"
                  onClick={() => setChosen(p.provisionId)}
                >
                  <span className="w-16 shrink-0 tabular-nums text-muted-foreground">{p.provisionId}</span>
                  <span>{p.title ?? ""}</span>
                </button>
              ))}
            </div>
          ) : data.text ? (
            <div className="whitespace-pre-wrap">{data.text}</div>
          ) : (
            <p className="text-muted-foreground">{t(data.licensed ? "guidelinesPage.provision.licensed" : "guidelinesPage.provision.noText")}</p>
          )}
        </div>
        {chosen || data?.officialUrl ? (
          <div className="flex items-center gap-2">
            {chosen && contents ? (
              <Button variant="ghost" size="sm" onClick={() => setChosen(null)}>
                <ArrowLeft size={14} className="mr-1" />
                {t("guidelinesPage.provision.back")}
              </Button>
            ) : null}
            <span className="flex-1" />
            {data?.officialUrl ? (
              <Button asChild variant="outline" size="sm">
                <a href={data.officialUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink size={14} className="mr-1" />
                  {t("guidelinesPage.provision.open", { label: data.officialLabel ?? data.officialUrl })}
                </a>
              </Button>
            ) : null}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
